import type { PublicWebformConfig } from './types'

// The iframe is served from forms.* (S3 + CloudFront with SPA fallback), while
// the public endpoints live on the NestJS service. Without an explicit base,
// fetches like `/public/webforms/<id>` hit the bucket's SPA fallback and come
// back as the iframe HTML with a 200 — same-origin would only work if the same
// host fronted both.
//
// Override via `<meta name="webform-api-base" content="...">` for local dev or
// for environments we haven't mapped here.
function apiBase(): string {
    if (typeof document !== 'undefined') {
        const meta = document.querySelector<HTMLMetaElement>('meta[name="webform-api-base"]')
        const override = meta?.content?.trim()
        if (override) return override.replace(/\/$/, '')
    }
    if (typeof window === 'undefined') return ''
    const host = window.location.hostname
    if (host === 'forms.coherenthealthcare.com') return 'https://service.coherenthealthcare.com'
    if (host === 'forms.test.coherenthealthcare.com') return 'https://service.test.coherenthealthcare.com'
    // Unknown host (local dev, preview, custom mapping): same-origin.
    return ''
}

export async function fetchConfig(publicId: string): Promise<PublicWebformConfig> {
    const res = await fetch(`${apiBase()}/public/webforms/${encodeURIComponent(publicId)}`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
    })
    if (!res.ok) {
        if (res.status === 404) throw new WebformNotFound()
        throw new Error(`Failed to load form (${res.status})`)
    }
    return await res.json()
}

export type SubmitPayload = {
    firstName: string
    lastName: string
    email: string
    mobile: string
    customAnswers: Record<string, string | boolean>
    acceptedPrivacyPolicy: boolean
    honeypot: string
    idempotencyKey: string
}

export async function submitForm(publicId: string, payload: SubmitPayload): Promise<void> {
    const res = await fetch(`${apiBase()}/public/webforms/${encodeURIComponent(publicId)}/submissions`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    if (res.status === 204) return
    if (res.status === 429) throw new Error("You're sending too many submissions. Please try again in a minute.")
    if (res.status === 400) {
        const body = await res.json().catch(() => null)
        const message = typeof body?.message === 'string' ? body.message
            : body?.details ? JSON.stringify(body.details)
            : 'Please check the form and try again.'
        throw new Error(message)
    }
    if (res.status === 403) throw new Error('This form is no longer accepting submissions.')
    throw new Error(`Submission failed (${res.status})`)
}

export class WebformNotFound extends Error {
    constructor() {
        super('Webform not found')
        this.name = 'WebformNotFound'
    }
}
